import React from 'react';
import {
  Paper,
  PaperProps,
  Typography,
  Button,
  Grid,
  ButtonProps,
  Stack,
  Box,
} from '@mui/material';

import { TP_COLORS } from '../../../../constants';
import { getThemeMode } from '../../../../store/ConfigSlice';
import {
  ISubmission,
  ISubmissionQuestion,
} from '../../../../store/SubmissionSlice';
import { IQuestion } from '../../../../store/QuestionSlice';
import { useAppSelector } from '../../../../hooks';
import { isMobileScreen } from '../../../../helper';

export interface IQuestionGridBox {
  paperProps?: PaperProps;
  submission: ISubmission;
  question: IQuestion;
  selectedQuestion: ISubmissionQuestion | undefined;
  onQuestionChange: (questionData: ISubmissionQuestion) => void;
  onQuizComplete?: () => void;
}

// STYLES
const defaultContainerStyles: PaperProps['sx'] = {
  borderRadius: '7px',
  p: '12px',
  boxShadow: 'unset',
};

const gridButtonSize = '30px';

const gridButtonStyles: ButtonProps['sx'] = {
  minWidth: 'unset',
  padding: '0',
  width: gridButtonSize,
  height: gridButtonSize,
  backgroundColor: 'transparent',
};

const QuestionGridBox: React.FC<IQuestionGridBox> = ({
  paperProps,
  submission,
  question,
  selectedQuestion,
  onQuestionChange,
  onQuizComplete,
}) => {
  // HOOKS
  const themeMode = useAppSelector(getThemeMode);

  // BOOLEANS
  const isMobile = isMobileScreen();

  // RENDER
  return (
    <Paper
      {...paperProps}
      sx={
        paperProps && paperProps.sx
          ? { ...defaultContainerStyles, ...paperProps.sx }
          : defaultContainerStyles
      }
    >
      <Typography variant="subtitle2" fontWeight={600}>
        Quiz Navigation ({submission.questions.length})
      </Typography>
      <div style={{ padding: '15px 0' }}>
        <Grid container spacing={1}>
          {submission.questions.map((questionData, index) => {
            const questionNumber = questionData.order + 1;
            const isSelected =
              question &&
              selectedQuestion &&
              selectedQuestion.question === questionData.question &&
              question._id === selectedQuestion.question;

            // STYLES
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const _styles: any = { ...gridButtonStyles };
            const _underLineStyles = {
              mt: '5px',
              minWidth: gridButtonSize,
              minHeight: '2.5px',
              backgroundColor: 'transparent',
              borderRadius: '10px',
            };

            if (isSelected) {
              _styles.backgroundColor =
                themeMode === 'dark'
                  ? 'rgba(255,255,255,0.5)'
                  : 'rgba(0,0,0,0.05)';
              _underLineStyles.backgroundColor = 'success.main';
            }

            const setCorrectStyles = () => {
              _styles.backgroundColor = 'success.light';
              _styles.borderColor = 'success.main';
              _styles['&:hover'] = { backgroundColor: 'success.light' };
              _styles.color =
                themeMode === 'light' ? 'success.main' : TP_COLORS.white;
            };

            const setInCorrectStyles = () => {
              _styles.backgroundColor = 'error.light';
              _styles.borderColor = 'error.main';
              _styles['&:hover'] = { backgroundColor: 'error.light' };
              _styles.color =
                themeMode === 'light' ? 'error.main' : TP_COLORS.white;
              if (isSelected) {
                _underLineStyles.backgroundColor = 'error.main';
              }
            };

            if (submission.status === 'completed') {
              if (questionData.status !== 'skipped' && questionData.answer) {
                if (questionData.answer?.isCorrect === true) {
                  setCorrectStyles();
                }
                if (questionData.answer?.isCorrect === false) {
                  setInCorrectStyles();
                }
              }

              if (questionData.isOpenEnded) {
                if (questionData.openEnded?.score) {
                  setCorrectStyles();
                }
                if (questionData.openEnded?.score === 0) {
                  setInCorrectStyles();
                }
              }
            }

            return (
              <Grid
                key={index}
                item
                xs={2}
                sm={1}
                md={isMobile ? 1 : 3}
                lg={isMobile ? 1 : 2}
              >
                <Stack alignItems="center">
                  <Button
                    color="inherit"
                    variant="outlined"
                    size="small"
                    sx={_styles}
                    onClick={() => onQuestionChange(questionData)}
                  >
                    {questionNumber}
                  </Button>
                  <Box sx={_underLineStyles} />
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      </div>
      {onQuizComplete && (
        <Stack alignItems="center">
          <Button
            fullWidth
            disableElevation
            size="small"
            color="primary"
            variant="contained"
            disabled={submission.status === 'completed'}
            sx={{
              // color: TP_COLORS.black,
              fontWeight: 600,
              maxWidth: { xs: 'unset', sm: '400px' },
            }}
            onClick={onQuizComplete}
          >
            {submission.status === 'completed' ? 'Completed' : 'Submit Quiz'}
          </Button>
        </Stack>
      )}
    </Paper>
  );
};

export default QuestionGridBox;
